import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Switch } from "@headlessui/react";
import { getTime, isValid } from "date-fns";
import { fromJS } from "immutable";
import { Check, Eye, EyeOff, Info, Lock, LockOpen } from "lucide-react";
import ActivityAction from "../actions/ActivityAction";
import LayoutAction from "../actions/LayoutAction";
import { uploadFileLogActivity, updateActivity } from "../api/ProjectApi";
import { getAppLink, getCurrentAzureOwner, getCurrentGoogleOwner } from "../api/AppLinkApi";
import { Button, Modal, Tooltip } from "../components/ui";
import { ActivityTypes } from "../constants/action-types";
import { getBaseUrl, isGmailContext } from "../api/api";
import InfoDialog from "./InfoDialog";
import TaskAction from "../actions/TaskAction";
import { useQuery } from "../AppLayout";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import ActivityForm from "../activities/components/ActivityForm";
import TaskForm from "../activities/components/TaskForm";
import NoteForm from "../activities/components/NoteForm";
import FileUploadForm from "../activities/components/FileUploadForm";
import EmailForm from "../activities/components/EmailForm";
import LogEmailForm from "../activities/components/LogEmailForm";
import PriceForm from "../activities/components/PriceForm";
import VariationForm from "../activities/components/VariationForm";
import { SnippetList, SnippetForm } from "../activities/components/SnippetManagerModal";
import { getFilterActivity } from "../main/Workspace";
import { sanitizeFileName } from "../ai-content/helpers";

const ActivityDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, itemId: currentItemId }: any = useParams();
  const currentParams = useQuery();
  const projectView = currentParams.get("view") || "GRID";
  const projectContentType = currentParams.get("type") || "SALE";
  const appView = currentParams.get("key") || "";

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const pagination = useSelector((state: any) => state.taskReducer.getIn(["pagination"]));
  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const sortFields = useSelector((state: any) => state.taskReducer.getIn(["sorts"]));
  const upcomingFilters =
    useSelector((state: any) => state.activityReducer.get("upcomingFilters")) || fromJS({});
  const show = useSelector((state: any) => state.activityReducer.get("show"));
  const activityId = useSelector((state: any) => state.activityReducer.getIn(["activity", "id"]));
  const itemId = useSelector((state: any) => state.activityReducer.getIn(["activity", "itemId"]));
  const eventData = useSelector((state: any) =>
    state.activityReducer.getIn(["activity", "event", "data"])
  );

  const activityType = eventData.get("activityType");
  const tracking = eventData.get("tracking") || false;
  const visibility = eventData.get("visibility") || "Shared";
  const isEditMode = (activityId || "") !== "";

  const [appGoogleData, setAppGoogleData] = useState<any>(fromJS({}));
  const [appAzureData, setAppAzureData] = useState<any>(fromJS({}));

  const [newEventData, setNewEventData] = useState<any>(fromJS({}));
  const [mentionedMembers, setMentionedMembers] = useState([]);
  const [createMore, setCreateMore] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [massSending, setMassSending] = useState(false);
  const [snippetView, setSnippetView] = useState<{
    mode: "hidden" | "list" | "create";
    prefill?: { subject?: string; description?: string };
    editTarget?: any;
  }>({ mode: "hidden" });

  const editorInputDescriptionRef = useRef(null);
  const editorInputPrivateDescriptionRef = useRef(null);

  // connected = token stored in DB; tokenExpired = token 403'd when we verified it
  const hasGmail = appGoogleData.get("connected") || false;
  const googleTokenExpired = appGoogleData.get("tokenExpired") || false;
  const hasOutlook = appAzureData.get("connected") || false;

  useEffect(() => {
    loadGoogleData();
    loadAzureData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (show && !isEditMode) {
      setNewEventData(eventData);
      // Auto-enable Mass sending when opened from bulk select
      setMassSending(eventData.get("sendBulkEmail") || false);
    }
  }, [show, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogleData = () => {
    getAppLink("google")
      .then((data) => {
        if (data.connected) {
          getCurrentGoogleOwner()
            .then((dataUser) => {
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: dataUser.name,
                  email: dataUser.email,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.info("Failed to get Google owner:", error);
              // Token expired/invalid — keep connected:true (still in DB)
              // but flag tokenExpired so UI can show a warning
              setAppGoogleData(
                fromJS({
                  app: "google",
                  name: t("Anonymous"),
                  email: "",
                  tokenExpired: true,
                  ...data,
                })
              );
            });
        } else {
          setAppGoogleData(
            fromJS({
              app: "google",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.info("Failed to get Google app link:", error);
        // Set default state when app link fetch fails
        setAppGoogleData(
          fromJS({
            app: "google",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const loadAzureData = () => {
    getAppLink("azure")
      .then((data) => {
        if (data.connected) {
          getCurrentAzureOwner()
            .then((dataUser) => {
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: dataUser.displayName,
                  email: dataUser.mail,
                  ...data,
                })
              );
            })
            .catch((error) => {
              console.error("Failed to get Azure owner:", error);
              // Set state to disconnected when API fails
              setAppAzureData(
                fromJS({
                  app: "azure",
                  name: t("Anonymous"),
                  email: "",
                  connected: false,
                  ...data,
                })
              );
            });
        } else {
          setAppAzureData(
            fromJS({
              app: "azure",
              name: t("Anonymous"),
              email: "",
              ...data,
            })
          );
        }
      })
      .catch((error) => {
        console.error("Failed to get Azure app link:", error);
        // Set default state when app link fetch fails
        setAppAzureData(
          fromJS({
            app: "azure",
            name: t("Anonymous"),
            email: "",
            connected: false,
          })
        );
      });
  };

  const reloadActivities = () => {
    const page = pagination.getIn(["number"]);
    const pageSize = pagination.getIn(["size"]);
    const filters = taskFilters.toJS();
    const sorts = sortFields.toJS();
    const filterByView = getFilterActivity(appView, currentUser);
    dispatch(
      TaskAction.searchItems({
        projectId,
        page,
        pageSize,
        filters: {
          ...filters,
          ...filterByView,
        },
        sorts,
        types: [projectContentType],
        view: projectView,
      })
    );
  };

  const getVisibility = (visibility: string) => {
    switch (visibility) {
      case "Shared":
        return (
          <div className="px-2 py-1 flex items-center rounded-full text-xs font-semibold bg-green-200">
            <LockOpen className="h-3 w-3 text-green-800" />
            <span className="ml-2 text-green-800">{visibility}</span>
          </div>
        );
      case "Private":
        return (
          <div className="px-2 py-1 flex items-center rounded-full text-xs font-semibold bg-red-200">
            <Lock className="h-3 w-3 text-red-800" />
            <span className="ml-2 text-red-800">{visibility}</span>
          </div>
        );
      default:
        return <></>;
    }
  };

  const getTracking = (tracking: boolean) => {
    if (tracking) {
      return (
        <div className="px-2 py-1 flex items-center rounded-full text-xs font-semibold bg-blue-200">
          <Eye className="h-3 w-3 text-blue-800" />
          <span className="ml-2 text-blue-800">{t("Tracking enabled")}</span>
        </div>
      );
    } else {
      return (
        <div className="px-2 py-1 flex items-center rounded-full text-xs font-semibold bg-gray-200">
          <Eye className="h-3 w-3 text-gray-800" />
          <span className="ml-2 text-gray-800">{t("Tracking disabled")}</span>
        </div>
      );
    }
  };

  const renderTitle = () => {
    if (isEditMode) {
      switch (activityType) {
        case ActivityTypes.email:
        case ActivityTypes.sendEmail:
        case ActivityTypes.sendDraftEmail:
        case ActivityTypes.sendScheduledEmail:
        case ActivityTypes.recordSentEmail:
          return t("Sent Email");
        case ActivityTypes.logEmail:
          return t("Log Email");
        case ActivityTypes.meeting:
          return t("Meeting");
        case ActivityTypes.call:
          return t("Call");
        case ActivityTypes.deadline:
          return t("Deadline");
        case ActivityTypes.task:
          return t("Edit Task");
        case ActivityTypes.commented:
          return t("Edit Note");
        case ActivityTypes.file:
          return t("Upload File");
        case ActivityTypes.lunch:
          return t("Lunch");
        case ActivityTypes.price:
          return t("Price");
        case ActivityTypes.variation:
          return t("Variation");
        default:
          return t("Activity");
      }
    } else {
      switch (activityType) {
        case ActivityTypes.email:
        case ActivityTypes.sendEmail:
        case ActivityTypes.sendDraftEmail:
        case ActivityTypes.sendScheduledEmail:
        case ActivityTypes.recordSentEmail:
          return t("Compose New Email");
        case ActivityTypes.logEmail:
          return t("Add Log Email");
        case ActivityTypes.meeting:
          return t("Add New Meeting");
        case ActivityTypes.call:
          return t("Add New Call");
        case ActivityTypes.deadline:
          return t("Add New Deadline");
        case ActivityTypes.task:
          return t("Add New Task");
        case ActivityTypes.commented:
          return t("Add New Note");
        case ActivityTypes.file:
          return t("Upload File");
        case ActivityTypes.lunch:
          return t("Add New Lunch");
        case ActivityTypes.price:
          return t("Add New Price");
        case ActivityTypes.variation:
          return t("Add New Variation");
        default:
          return t("Add New Activity");
      }
    }
  };

  const renderSubtitle = () => {
    if (isEditMode) {
      if (
        activityType === ActivityTypes.sendEmail ||
        activityType === ActivityTypes.sendDraftEmail ||
        activityType === ActivityTypes.sendScheduledEmail ||
        activityType === ActivityTypes.recordSentEmail
      ) {
        return (
          <div className="flex items-center justify-center gap-4">
            {getVisibility(eventData.get("visibility"))}
            {getTracking(eventData.get("tracking"))}
          </div>
        );
      }
    } else {
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const data = eventData.toJS();

    if (!data.summary?.trim()) {
      if (
        activityType == ActivityTypes.meeting ||
        activityType == ActivityTypes.call ||
        activityType == ActivityTypes.deadline ||
        activityType == ActivityTypes.commented
      ) {
        errors.summary = t("Title is required");
      } else if (activityType == ActivityTypes.task) {
        errors.summary = t("Task is required");
      } else if (activityType == ActivityTypes.sendEmail) {
        errors.summary = t("Subject is required");
      } else if (activityType == ActivityTypes.sendDraftEmail) {
        errors.summary = t("Subject is required");
      } else if (activityType == ActivityTypes.sendScheduledEmail) {
        errors.summary = t("Subject is required");
      } else if (activityType == ActivityTypes.recordSentEmail) {
        errors.summary = t("Subject is required");
      } else if (activityType == ActivityTypes.price) {
        errors.summary = t("Price name is required");
      } else if (activityType == ActivityTypes.variation) {
        errors.summary = t("Variation name is required");
      } else {
        errors.summary = t("Field is required");
      }
    }
    if (!data.description?.trim()) {
      if (
        activityType == ActivityTypes.logEmail ||
        activityType == ActivityTypes.sendEmail ||
        activityType == ActivityTypes.sendDraftEmail ||
        activityType == ActivityTypes.sendScheduledEmail ||
        activityType == ActivityTypes.recordSentEmail ||
        activityType == ActivityTypes.commented
      ) {
        errors.description = t("Content is required");
      }
    }
    if (!data.organizer?.trim()) {
      if (activityType == ActivityTypes.logEmail) {
        errors.organizer = t("Sender is required");
      }
    }
    if (!data.attendees || data.attendees.length === 0) {
      if (activityType == ActivityTypes.meeting || activityType == ActivityTypes.logEmail) {
        errors.attendees = t("Attendees are required");
      }
      if (
        activityType == ActivityTypes.sendEmail ||
        activityType == ActivityTypes.sendDraftEmail ||
        activityType == ActivityTypes.sendScheduledEmail ||
        activityType == ActivityTypes.recordSentEmail
      ) {
        if (data.sendBulkEmail) {
          if (!data.itemIds) {
            errors.attendees = t("Attendees are required");
          }
        } else {
          errors.attendees = t("Attendees are required");
        }
      }
    }
    if (!data.assignees || data.assignees.length === 0) {
      if (activityType == ActivityTypes.task) {
        errors.assignees = t("Assignees are required");
      }
    }
    if (!data.sendBulkEmail && (data.item?.value == undefined || data.item?.value === "")) {
      errors.linkItem = t("Please select an item to link this activity to");
    }

    // Validate start date and time
    if (!data.start || !data.startTime) {
      errors.startTimeEndTime = t("Please select start date and time");
    }

    // Validate end date and time
    if (!data.end || !data.endTime) {
      errors.startTimeEndTime = t("Please select end date and time");
    }

    try {
      const start = new Date(`${data.start}T${data.startTime}`);
      const end = new Date(`${data.end}T${data.endTime}`);

      if (!isValid(start)) {
        errors.startTimeEndTime = t("Invalid start datetime");
      }

      if (!isValid(end)) {
        errors.startTimeEndTime = t("Invalid end datetime");
      }

      if (activityType == ActivityTypes.deadline || activityType == ActivityTypes.logEmail) {
        if (getTime(start) > getTime(end)) {
          errors.startTimeEndTime = t("End time must be greater than start time");
        }
      } else {
        if (getTime(start) >= getTime(end)) {
          errors.startTimeEndTime = t("End time must be greater than start time");
        }
      }
    } catch {
      errors.startTimeEndTime = t("Invalid datetime format");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderIconCreateOrUpdate = () => {
    if (hasGmail) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (hasOutlook) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const handleOnChange = (fieldId, fieldValue) => {
    dispatch(ActivityAction.changeActivityData(fieldId, fieldValue));
  };

  const handleFileChange = (files: File[]) => {
    if (itemId && files && files.length > 0) {
      const file = files[0];
      const newFile = new File([file], sanitizeFileName(Date.now() + "-" + file.name), {
        type: file.type,
        lastModified: file.lastModified,
      });
      uploadFileLogActivity(itemId, newFile, visibility).then(() => {
        handleOnClose();
        if (currentItemId) {
          dispatch(
            ActivityAction.searchActivities({
              itemId: currentItemId,
              scope: "item",
              includeDependencies:
                projectContentType === "COMPANY" ||
                projectContentType === "CONTACT" ||
                projectContentType === "SALE",
            })
          );
        }
        dispatch(
          LayoutAction.setNotification({
            title: t("Activity"),
            type: "success",
            message: t("Uploaded file"),
          })
        );
      });
    }
  };

  const handleCreateOrUpdate = () => {
    if (!validateForm()) {
      return;
    }
    if (isEditMode) {
      updateActivity(activityId, {
        summary: eventData.get("summary"),
        start: eventData.get("start"),
        startTime: eventData.get("startTime"),
        end: eventData.get("end"),
        endTime: eventData.get("endTime"),
        assignees: eventData.get("assignees")?.toJS() || [],
        attendees: eventData.get("attendees")?.toJS() || [],
        description: eventData.get("description"),
        privateDescription: eventData.get("privateDescription"),
        done: eventData.get("done"),
        item: eventData.get("item"),
      })
        .then(() => {
          if (currentItemId) {
            dispatch(
              ActivityAction.searchActivities({
                itemId: currentItemId,
                scope: "item",
                includeDependencies:
                  projectContentType === "COMPANY" ||
                  projectContentType === "CONTACT" ||
                  projectContentType === "SALE",
              })
            );
          }
          if (
            projectContentType === "ACTIVITY" &&
            (appView === "Tasks" ||
              appView === "Notes" ||
              appView === "Drafts" ||
              appView === "Outbox" ||
              appView === "Tracked")
          ) {
            reloadActivities();
          }
          if (isGmailContext()) {
            dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
          }
          handleOnClose();
          if (activityType === ActivityTypes.commented) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated note"),
              })
            );
          } else if (activityType === ActivityTypes.task) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated task"),
              })
            );
          } else if (activityType === ActivityTypes.sendEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated email"),
              })
            );
          } else if (activityType === ActivityTypes.sendDraftEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated email"),
              })
            );
          } else if (activityType === ActivityTypes.sendScheduledEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated email"),
              })
            );
          } else if (activityType === ActivityTypes.recordSentEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated email"),
              })
            );
          } else if (activityType === ActivityTypes.logEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated log email"),
              })
            );
          } else if (activityType === ActivityTypes.price) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated price"),
              })
            );
          } else if (activityType === ActivityTypes.variation) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "success",
                message: t("Updated variation"),
              })
            );
          }
        })
        .catch(() => {
          if (activityType === ActivityTypes.commented) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update note"),
              })
            );
          } else if (activityType === ActivityTypes.task) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update task"),
              })
            );
          } else if (activityType === ActivityTypes.sendEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update email"),
              })
            );
          } else if (activityType === ActivityTypes.sendDraftEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update email"),
              })
            );
          } else if (activityType === ActivityTypes.sendScheduledEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update email"),
              })
            );
          } else if (activityType === ActivityTypes.recordSentEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update email"),
              })
            );
          } else if (activityType === ActivityTypes.logEmail) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update log email"),
              })
            );
          } else if (activityType === ActivityTypes.price) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update price"),
              })
            );
          } else if (activityType === ActivityTypes.variation) {
            dispatch(
              LayoutAction.setNotification({
                title: "Activity",
                type: "error",
                message: t("Failed to update variation"),
              })
            );
          }
        });
    } else {
      let action = "createActivity";
      if (activityType === ActivityTypes.commented) {
        action = "commented";
      } else if (activityType === ActivityTypes.sendEmail) {
        action = "sendEmail";
      } else if (activityType === ActivityTypes.sendDraftEmail) {
        action = "sendDraftEmail";
      } else if (activityType === ActivityTypes.sendScheduledEmail) {
        action = "sendScheduledEmail";
      } else if (activityType === ActivityTypes.recordSentEmail) {
        action = "recordSentEmail";
      }
      let assignees = "";
      if (eventData.get("assignees") && eventData.get("assignees").toJS().length > 0) {
        assignees = eventData.get("assignees").toJS().join(",");
      }
      let attendees = "";
      if (eventData.get("attendees") && eventData.get("attendees").toJS().length > 0) {
        attendees = eventData.get("attendees").toJS().join(",");
      }
      const request = {
        type: activityType,
        data: {
          ...eventData.toJS(),
          assignees,
          attendees,
        },
        action,
        mentionedMembers,
        person: currentUser.get("accountId"),
      };
      const itemIdData = itemId || eventData.getIn(["item", "value"]);
      const itemIds = eventData.get("itemIds") || "";
      const sendBulkEmail = eventData.get("sendBulkEmail") || false;

      const validationErrors = [];
      if (!request.action) validationErrors.push("Missing action");
      if (!request.person) validationErrors.push("Missing person/accountId");
      if (!request.type) validationErrors.push("Missing activity type");

      let isBulkActivity = false;
      // Bulk activity send email
      if (activityType === ActivityTypes.sendEmail && sendBulkEmail) {
        isBulkActivity = true;
        if (!itemIds) {
          validationErrors.push("Missing itemIds for bulk activity");
        }
      }

      if (validationErrors.length > 0) {
        dispatch(
          LayoutAction.setNotification({
            title: "Activity",
            type: "error",
            message: `Validation failed: ${validationErrors.join(", ")}`,
          })
        );
        return;
      }
      const apiItemId = isBulkActivity ? "" : itemIdData;
      dispatch(ActivityAction.createActivity(apiItemId, request))
        .then(() => {
          if (currentItemId) {
            dispatch(
              ActivityAction.searchActivities({
                itemId: currentItemId,
                scope: "item",
                includeDependencies:
                  projectContentType === "COMPANY" ||
                  projectContentType === "CONTACT" ||
                  projectContentType === "SALE",
              })
            );
          }
          if (
            projectContentType === "ACTIVITY" &&
            (appView === "Tasks" ||
              appView === "Notes" ||
              appView === "Drafts" ||
              appView === "Outbox" ||
              appView === "Tracked")
          ) {
            reloadActivities();
          }
          if (isGmailContext()) {
            dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
          }
          if (!createMore) {
            handleOnClose();
          } else {
            setMentionedMembers([]);
            setValidationErrors({});
            dispatch(ActivityAction.resetActivityDialog(newEventData.toJS()));
            if (editorInputDescriptionRef.current) {
              editorInputDescriptionRef.current.root.innerHTML = "";
            }
            if (editorInputPrivateDescriptionRef.current) {
              editorInputPrivateDescriptionRef.current.root.innerHTML = "";
            }
          }
          dispatch(
            LayoutAction.setNotification({
              title: "Activity",
              type: "success",
              message: isBulkActivity ? "Bulk activity scheduled" : "Scheduled an activity",
            })
          );
        })
        .catch((error) => {
          if (error?.response) {
          } else if (error?.request) {
            console.error("Network error - no response received:", error.request);
          } else {
            console.error("Request setup error:", error.message);
          }

          if (!createMore) {
            handleOnClose();
          } else {
            setMentionedMembers([]);
            setValidationErrors({});
            dispatch(ActivityAction.resetActivityDialog(newEventData.toJS()));
            if (editorInputDescriptionRef.current) {
              editorInputDescriptionRef.current.root.innerHTML = "";
            }
            if (editorInputPrivateDescriptionRef.current) {
              editorInputPrivateDescriptionRef.current.root.innerHTML = "";
            }
          }

          // Provide more specific error message if available
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Cannot create activity";

          dispatch(
            LayoutAction.setNotification({
              title: "Activity",
              type: "error",
              message: `Error (${error?.response?.status || "Unknown"}): ${errorMessage}`,
            })
          );
        });
    }
  };

  const handleOnClose = () => {
    setMentionedMembers([]);
    setValidationErrors({});
    setCreateMore(false);
    dispatch(ActivityAction.hideActivityDialog());
  };

  const renderForm = () => {
    if (
      activityType === ActivityTypes.meeting ||
      activityType === ActivityTypes.call ||
      activityType === ActivityTypes.deadline
    ) {
      return (
        <ActivityForm
          isEditMode={isEditMode}
          appGoogleData={appGoogleData}
          appAzureData={appAzureData}
          projectId={project.get("id")}
          activityType={activityType}
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          onChangeMentionedMembers={(mentionedMembers) => {
            setMentionedMembers(mentionedMembers);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
          editorInputPrivateDescriptionRef={editorInputPrivateDescriptionRef}
        />
      );
    } else if (activityType === ActivityTypes.task) {
      return (
        <TaskForm
          isEditMode={isEditMode}
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          validationErrors={validationErrors}
        />
      );
    } else if (activityType === ActivityTypes.commented) {
      return (
        <NoteForm
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          onChangeMentionedMembers={(mentionedMembers) => {
            setMentionedMembers(mentionedMembers);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
        />
      );
    } else if (activityType === ActivityTypes.file) {
      return <FileUploadForm handleFileChange={handleFileChange} />;
    } else if (
      activityType === ActivityTypes.email ||
      activityType === ActivityTypes.sendEmail ||
      activityType === ActivityTypes.sendDraftEmail ||
      activityType === ActivityTypes.sendScheduledEmail ||
      activityType === ActivityTypes.recordSentEmail
    ) {
      return (
        <EmailForm
          isEditMode={isEditMode}
          appGoogleData={appGoogleData}
          appAzureData={appAzureData}
          projectId={project.get("id")}
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
          onCreateSnippet={() => {
            const js = eventData.toJS();
            setSnippetView({
              mode: "create",
              prefill: {
                subject: js.summary ?? "",
                description: js.description ?? "",
              },
            });
          }}
          onManageSnippets={() => setSnippetView({ mode: "list" })}
          massSending={massSending}
          navigateToUrl={(url) => {
            navigate(url);
            handleOnClose();
          }}
        />
      );
    } else if (activityType === ActivityTypes.logEmail) {
      return (
        <LogEmailForm
          isEditMode={isEditMode}
          projectId={project.get("id")}
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
        />
      );
    } else if (activityType === ActivityTypes.price) {
      return (
        <PriceForm
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          onChangeMentionedMembers={(mentionedMembers) => {
            setMentionedMembers(mentionedMembers);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
        />
      );
    } else if (activityType === ActivityTypes.variation) {
      return (
        <VariationForm
          eventData={eventData.toJS()}
          onDataChange={(fieldId, fieldValue) => {
            handleOnChange(fieldId, fieldValue);
          }}
          onChangeMentionedMembers={(mentionedMembers) => {
            setMentionedMembers(mentionedMembers);
          }}
          validationErrors={validationErrors}
          editorInputDescriptionRef={editorInputDescriptionRef}
        />
      );
    }
    return null;
  };

  const renderLeftFooter = () => {
    if (activityType === ActivityTypes.file) {
      if (isEditMode) return null;
      return (
        <div className="flex items-center space-x-2">
          <Switch
            checked={visibility === "Shared"}
            onChange={(checked) => {
              if (checked) {
                handleOnChange("visibility", "Shared");
              } else {
                handleOnChange("visibility", "Private");
              }
            }}
            className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-red-600 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-green-600"
          >
            <Lock
              className={`absolute z-1 top-1/2 -translate-y-1/2 left-0.5 h-2.5 w-2.5 ${
                visibility === "Shared" ? "text-white" : "text-red-600"
              }`}
            />
            <LockOpen
              className={`absolute z-1 top-1/2 -translate-y-1/2 right-0.5 h-2.5 w-2.5 ${
                visibility === "Shared" ? "text-green-600" : "text-white"
              }`}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
            />
          </Switch>
          <span className="text-gray-500 text-sm font-semibold">
            {visibility === "Shared" ? t("Shared") : t("Private")}
          </span>
        </div>
      );
    }
    if (
      activityType === ActivityTypes.sendEmail ||
      activityType === ActivityTypes.sendDraftEmail ||
      activityType === ActivityTypes.sendScheduledEmail ||
      activityType === ActivityTypes.recordSentEmail
    ) {
      if (isEditMode) return null;
      return (
        <div className="flex items-center gap-4 flex-wrap">
          {/* Shared / Private toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              checked={visibility === "Shared"}
              onChange={(checked) => {
                if (checked) {
                  handleOnChange("visibility", "Shared");
                } else {
                  handleOnChange("visibility", "Private");
                }
              }}
              className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-red-600 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-green-600"
            >
              <Lock
                className={`absolute z-1 top-1/2 -translate-y-1/2 left-0.5 h-2.5 w-2.5 ${
                  visibility === "Shared" ? "text-white" : "text-red-600"
                }`}
              />
              <LockOpen
                className={`absolute z-1 top-1/2 -translate-y-1/2 right-0.5 h-2.5 w-2.5 ${
                  visibility === "Shared" ? "text-green-600" : "text-white"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">
              {visibility === "Shared" ? t("Shared") : t("Private")}
            </span>
          </div>
          {/* Tracking toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              checked={tracking}
              onChange={(value) => handleOnChange("tracking", value)}
              className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600"
            >
              <EyeOff
                className={`absolute z-1 top-1/2 -translate-y-1/2 left-0.5 h-2.5 w-2.5 ${
                  tracking ? "text-white" : "text-blue-600"
                }`}
              />
              <Eye
                className={`absolute z-1 top-1/2 -translate-y-1/2 right-0.5 h-2.5 w-2.5 ${
                  tracking ? "text-blue-600" : "text-gray-600"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">{t("Tracking")}</span>
          </div>
        </div>
      );
    }
    return (
      <>
        {!isEditMode && (
          <div className="flex items-center space-x-2">
            <Switch
              checked={createMore}
              onChange={setCreateMore}
              className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600"
            >
              <Check
                className={`absolute z-1 top-1 right-1 h-3 w-3 ${
                  createMore ? "text-blue-600" : "text-gray-600"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">{t("Create more")}</span>
          </div>
        )}
      </>
    );
  };

  const isSendEmailCompose =
    !isEditMode &&
    (activityType === ActivityTypes.sendEmail ||
      activityType === ActivityTypes.sendDraftEmail ||
      activityType === ActivityTypes.sendScheduledEmail ||
      activityType === ActivityTypes.recordSentEmail);

  const isSnippetView = snippetView.mode !== "hidden";

  // ── Snippet inline content & footer ─────────────────────────────────────────
  const snippetContent = isSnippetView ? (
    <div className="p-4">
      {snippetView.mode === "list" ? (
        <SnippetList
          workspaceId={project?.get("id") ?? ""}
          onEdit={(s) => setSnippetView({ mode: "create", editTarget: s })}
          onCreate={() => setSnippetView({ mode: "create" })}
        />
      ) : (
        <SnippetForm
          workspaceId={project?.get("id") ?? ""}
          initial={snippetView.editTarget}
          prefill={!snippetView.editTarget ? snippetView.prefill : undefined}
          onSaved={() => setSnippetView({ mode: "list" })}
          onCancel={() =>
            snippetView.editTarget
              ? setSnippetView({ mode: "list" })
              : setSnippetView({ mode: "hidden" })
          }
        />
      )}
    </div>
  ) : null;

  const snippetFooter = isSnippetView ? (
    <div className="flex items-center justify-between">
      <div />
      <Button size="sm" variant="secondary" onClick={() => setSnippetView({ mode: "hidden" })}>
        {t("Close")}
      </Button>
    </div>
  ) : null;

  const modalContent = isSnippetView ? (
    snippetContent
  ) : isSendEmailCompose ? (
    // Email compose: full split-pane (no extra padding — RecipientsSidebar needs h-full)
    <div className="flex-1 flex h-full overflow-hidden">{renderForm()}</div>
  ) : (
    <div className="p-4">{renderForm()}</div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">{renderLeftFooter()}</div>
      <div className="flex items-center justify-end gap-3">
        {/* Mass sending toggle — right side, only for email compose */}
        {isSendEmailCompose && (
          <div className="flex items-center space-x-2 mr-1">
            <Switch
              checked={massSending}
              onChange={setMassSending}
              className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold flex items-center gap-1">
              {t("Mass sending")}
              <Tooltip
                text={t(
                  "When enabled, each email will be sent individually to each recipient. This option is required when using variables with multiple recipients."
                )}
                tooltipClassName="!whitespace-normal text-center leading-relaxed"
                tooltipStyle={{ width: "240px", lineHeight: "1.5" }}
              >
                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
              </Tooltip>
            </span>
          </div>
        )}
        <Button size="sm" variant="secondary" onClick={handleOnClose}>
          {t("Cancel")}
        </Button>
        {activityType !== ActivityTypes.file &&
        activityType !== ActivityTypes.sendEmail &&
        activityType !== ActivityTypes.sendDraftEmail &&
        activityType !== ActivityTypes.sendScheduledEmail &&
        activityType !== ActivityTypes.recordSentEmail ? (
          <Button
            size="sm"
            variant="primary"
            onClick={handleCreateOrUpdate}
            leftIcon={
              activityType !== ActivityTypes.task &&
              activityType !== ActivityTypes.commented &&
              activityType !== ActivityTypes.logEmail &&
              activityType !== ActivityTypes.price &&
              activityType !== ActivityTypes.variation
                ? renderIconCreateOrUpdate()
                : null
            }
          >
            {isEditMode ? t("Update") : t("Add")}
          </Button>
        ) : null}
        {activityType === ActivityTypes.sendEmail && !isEditMode ? (
          <Button
            size="sm"
            variant="primary"
            onClick={handleCreateOrUpdate}
            leftIcon={renderIconCreateOrUpdate()}
          >
            {t("Send")}
          </Button>
        ) : null}
        {(activityType === ActivityTypes.sendEmail ||
          activityType === ActivityTypes.sendDraftEmail ||
          activityType === ActivityTypes.sendScheduledEmail ||
          activityType === ActivityTypes.recordSentEmail) &&
        isEditMode ? (
          <Button size="sm" variant="primary" onClick={handleCreateOrUpdate}>
            {t("Update")}
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (!show) return null;

  if (
    (activityType === ActivityTypes.sendEmail || activityType === ActivityTypes.meeting) &&
    !isEditMode &&
    (!hasGmail || googleTokenExpired) &&
    !hasOutlook
  ) {
    let title = t("Connect your Google/Microsoft account");
    if (activityType === ActivityTypes.sendEmail) {
      title = t("Connect to compose email");
    } else if (activityType === ActivityTypes.meeting) {
      title = t("Connect to create a meeting");
    }
    return (
      <InfoDialog
        visible={show}
        width={500}
        alertIcon={<OctagonAlertIcon />}
        title={title}
        description={
          <div className="space-y-4">
            <div>
              {t(
                "You need to connect with your Google/Microsoft account to sync email and calendar"
              )}
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  navigate(
                    `/workspaces/${projectId}/settings/apps?key=Apps&appId=google&item=Gmail`
                  );
                  handleOnClose();
                }}
                leftIcon={
                  <img
                    className="w-4 h-4 object-contain"
                    src={`${getBaseUrl()}/static/images/google.png`}
                    alt="Google"
                  />
                }
              >
                {t("Connect with Google")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  navigate(
                    `/workspaces/${projectId}/settings/apps?key=Apps&appId=azure&item=Outlook`
                  );
                  handleOnClose();
                }}
                leftIcon={
                  <img
                    className="w-4 h-4 object-contain"
                    src={`${getBaseUrl()}/static/images/microsoft.png`}
                    alt="Microsoft"
                  />
                }
              >
                {t("Connect with Microsoft")}
              </Button>
            </div>
          </div>
        }
        onClose={handleOnClose}
      />
    );
  }

  // For email compose: use wide split-pane modal
  const isSendEmail =
    activityType === ActivityTypes.sendEmail ||
    activityType === ActivityTypes.sendDraftEmail ||
    activityType === ActivityTypes.sendScheduledEmail ||
    activityType === ActivityTypes.recordSentEmail;

  return (
    <>
      <Modal
        isOpen={show}
        onClose={handleOnClose}
        title={
          isSnippetView
            ? snippetView.mode === "list"
              ? t("Manage Snippets")
              : snippetView.editTarget
                ? t("Edit Snippet")
                : t("Create Snippet")
            : renderTitle()
        }
        subtitle={isSnippetView ? undefined : renderSubtitle()}
        className={
          isSendEmail && !isEditMode
            ? massSending
              ? "w-[900px] max-w-[95vw] max-h-[92vh]" // split-pane mass send
              : "w-[620px] max-w-[95vw] max-h-[92vh]" // compact single email
            : "w-auto max-h-[98vh]"
        }
        footer={isSnippetView ? snippetFooter : modalFooter}
      >
        {modalContent}
      </Modal>
    </>
  );
};

export default ActivityDialog;
